import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, Pencil, Trash2, Wallet, Landmark, CreditCard, TrendingUp, AlertTriangle } from 'lucide-react';
import { IconButton } from '@/components/ui/icon-button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useFABContext } from '@/components/common/FABContext';
import { createAccount, deleteAccount, fetchAccounts, updateAccount } from '@/features/accounts/accountsSlice';
import type { Account } from '@/lib/types';
import { AccountForm, type AccountFormValues } from '@/components/accounts/AccountForm';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import AccountDetailSheet from '@/components/accounts/AccountDetailSheet';
import PageGuide from '@/components/common/PageGuide';
import { accountsGuide } from '@/components/common/guideContent';


const accountTypeIcon: Record<Account['accountType'], JSX.Element> = {
  checking: <Landmark className="h-4 w-4" />,
  savings: <TrendingUp className="h-4 w-4" />,
  credit_card: <CreditCard className="h-4 w-4" />,
  investment: <TrendingUp className="h-4 w-4" />,
  loan: <CreditCard className="h-4 w-4" />,
  mortgage: <Landmark className="h-4 w-4" />,
  manual: <Wallet className="h-4 w-4" />,
  crypto: <TrendingUp className="h-4 w-4" />,
  other: <Wallet className="h-4 w-4" />,
};

export default function Accounts() {
  useDocumentTitle('Accounts');
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();

  const { accounts, isLoading: accountsLoading } = useAppSelector((state) => state.accounts);

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  // [T-FAB] Register quick-add action so the global FAB can open this form
  const { registerFABActions } = useFABContext();
  useEffect(() => {
    registerFABActions({ onAddAccount: () => { setSelectedAccount(null); setAccountDialogOpen(true); } });
    return () => registerFABActions({});
  }, [registerFABActions]);

  // [T-FAB-DRAFT] Auto-open add form when navigating via a draft link from FAB
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as { openForm?: boolean } | null)?.openForm) {
      setSelectedAccount(null);
      setAccountDialogOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [isSubmittingAccount, setIsSubmittingAccount] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null);
  const [detailAccount, setDetailAccount] = useState<Account | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Count transactions per account for cascade-delete warning
  const { transactions } = useAppSelector((state) => state.transactions);
  const txCountForAccount = useMemo(() => {
    if (!accountToDelete) return 0;
    return transactions.filter((t) => {
      const tx = t as { accountId?: string; account?: string };
      return tx.accountId === accountToDelete.id || tx.account === accountToDelete.id;
    }).length;
  }, [transactions, accountToDelete]);

  const openDetail = (account: Account) => {
    setDetailAccount(account);
    setDetailOpen(true);
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchAccounts());
  }, [dispatch]);

  const totals = useMemo(() => {
    const balance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const assets = accounts.filter((a) => a.balance >= 0).reduce((sum, acc) => sum + acc.balance, 0);
    const liabilities = accounts.filter((a) => a.balance < 0).reduce((sum, acc) => sum + acc.balance, 0);
    const manual = accounts.filter((a) => a.isManual).length;
    return { balance, assets, liabilities, manual };
  }, [accounts]);

  const handleAccountSubmit = async (values: AccountFormValues) => {
    setIsSubmittingAccount(true);
    try {
      const payload: Partial<Account> = {
        name: values.name,
        accountType: values.accountType,
        balance: values.balance,
        currency: values.currency,
        institutionName: values.institutionName || undefined,
        institution: values.institutionName || undefined,
        accountNumber: values.accountNumber || undefined,
        creditLimit: values.creditLimit,
        interestRate: values.interestRate,
        isManual: values.isManual,
        isActive: values.isActive,
        notes: values.notes || undefined,
        emoji: values.emoji || undefined,
        color: values.color || undefined,
      };

      if (selectedAccount) {
        await dispatch(updateAccount({ id: selectedAccount.id, data: payload })).unwrap();
        toast({ title: 'Account updated', description: `${values.name} has been saved.` });
      } else {
        await dispatch(createAccount(payload)).unwrap();
        toast({ title: 'Account created', description: `${values.name} is ready to use.` });
      }

      setAccountDialogOpen(false);
      setSelectedAccount(null);
    } catch (error: unknown) {
      toast({ title: 'Unable to save account', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setIsSubmittingAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    try {
      await dispatch(deleteAccount(accountToDelete.id)).unwrap();
      toast({ title: 'Account deleted', description: `${accountToDelete.name} removed.` });
    } catch (error: unknown) {
      toast({ title: 'Unable to delete account', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setAccountToDelete(null);
    }
  };

  const renderAccountsTable = () => {
    if (accountsLoading && accounts.length === 0) {
      return (
        <div className="space-y-2">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-4 rounded-md border p-4">
              <Skeleton className="h-9 w-9 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      );
    }

    if (accounts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <Wallet className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No accounts yet. Add your first account to get started.</p>
          <Button className="mt-4" onClick={() => setAccountDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Account
          </Button>
        </div>
      );
    }

    return (
      <>
      {/* Desktop table */}
      <div className="hidden md:table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Account</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow
              key={account.id}
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => openDetail(account)}
            >
              <TableCell className="flex items-center gap-3">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-lg border"
                  style={{ backgroundColor: account.color ? `${account.color}15` : '#f3f4f6' }}
                >
                  {account.emoji ? (
                    <span className="text-lg leading-none">{account.emoji}</span>
                  ) : (
                    accountTypeIcon[account.accountType]
                  )}
                </div>
                <div>
                  <div className="font-medium">{account.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {account.institutionName || account.institution || 'Manual entry'}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="capitalize">{account.accountType.replace('_', ' ')}</Badge>
              </TableCell>
              <TableCell className="text-right font-semibold">
                {formatCurrency(account.balance, account.currency || 'USD')}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={account.isActive ? 'outline' : 'secondary'}>
                    {account.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  {account.isManual && <Badge variant="secondary">Manual</Badge>}
                  {account.creditLimit ? (
                    <Badge variant="secondary">Limit {formatCurrency(account.creditLimit, account.currency || 'USD')}</Badge>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      variant="ghost"
                      size="icon"
                      tooltip="Account actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAccount(account);
                        setAccountDialogOpen(true);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAccountToDelete(account);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2">
        {accounts.map((account) => (
          <div
            key={account.id}
            className="flex items-center gap-3 p-3 rounded-lg border bg-card cursor-pointer hover:bg-muted/50 transition-colors min-h-[44px]"
            onClick={() => openDetail(account)}
          >
            <div
              className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg border"
              style={{ backgroundColor: account.color ? `${account.color}15` : '#f3f4f6' }}
            >
              {account.emoji ? (
                <span className="text-lg leading-none">{account.emoji}</span>
              ) : (
                accountTypeIcon[account.accountType]
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{account.name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="capitalize">{account.accountType.replace('_', ' ')}</span>
                <span>·</span>
                <Badge variant={account.isActive ? 'outline' : 'secondary'} className="text-[10px] px-1 py-0">
                  {account.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="font-semibold text-sm">
                {formatCurrency(account.balance, account.currency || 'USD')}
              </p>
            </div>
            <div className="flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    tooltip="Account actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAccount(account);
                      setAccountDialogOpen(true);
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountToDelete(account);
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
      </>
    );
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <PageGuide pageKey="accounts" content={accountsGuide} />
      <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Accounts</h2>
          <p className="text-sm md:text-base text-muted-foreground">Manage your financial accounts.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setAccountDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Account
          </Button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardDescription>Total Balance</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(totals.balance)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">All active accounts</CardContent>
        </Card>
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardDescription>Assets</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(totals.assets)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Positive balances</CardContent>
        </Card>
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardDescription>Liabilities</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(Math.abs(totals.liabilities))}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Negative balances</CardContent>
        </Card>
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardDescription>Manual Accounts</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{totals.manual}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Tracked without integrations</CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Accounts</CardTitle>
              <CardDescription>View, edit, and remove accounts.</CardDescription>
            </div>
            <Button size="sm" onClick={() => setAccountDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>{renderAccountsTable()}</CardContent>
      </Card>

      <AccountForm
        open={accountDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAccount(null);
          }
          setAccountDialogOpen(open);
        }}
        onSubmit={handleAccountSubmit}
        account={selectedAccount}
        isSubmitting={isSubmittingAccount}
      />

      {/* Account Detail Sheet */}
      <AccountDetailSheet
        account={detailAccount}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={(account) => {
          setSelectedAccount(account);
          setAccountDialogOpen(true);
        }}
        onDelete={(account) => setAccountToDelete(account)}
      />

      <AlertDialog open={!!accountToDelete} onOpenChange={(open) => !open && setAccountToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete account
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to delete <strong>{accountToDelete?.name ?? 'this account'}</strong>?
                  This action cannot be undone.
                </p>
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <p className="text-sm font-medium text-destructive">
                    ⚠️ All transactions associated with this account will also be permanently deleted.
                  </p>
                  {txCountForAccount > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      This account currently has <strong>{txCountForAccount}</strong> transaction{txCountForAccount !== 1 ? 's' : ''} that will be removed.
                    </p>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Account & Transactions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
